import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ParticipantStudyView } from "@leaply-interview/shared-types";
import { InterviewDisclaimers } from "../components/InterviewDisclaimers";
import { InterviewHero } from "../components/InterviewHero";
import { Listening } from "../components/Listening";
import { LiveCaption } from "../components/LiveCaption";
import { Orb } from "../components/Orb";
import { ApiError, initiateInterview } from "../lib/api";
import { decodeAutologin } from "../lib/autologin";
import { events, initAmplitude, setTestMode } from "../lib/amplitude";
import { saveSession } from "../lib/session";
import { type AgentStatus, useRealtimeAgent } from "../lib/useRealtimeAgent";
import { isValidEmail, validateEmail } from "../lib/validateEmail";

/** Coarse routing threshold for /complete vs /too-short (architecture §5.2).
 *  Per-study gates run server-side; this is the client-side pre-filter that
 *  splits "thanks!" from "ended too soon — try again". */
const VALID_MIN_SECONDS = 30;

/** /interview — entry point and active call (architecture §5.1).
 *
 * Stages:
 *   loading            initial param parse + initiate
 *   admin-email-form   ?studyId only — admin QA, no email in URL
 *   entry              initiate done, show Hero + Begin
 *   active             Begin clicked, useRealtimeAgent running
 *   error              terminal — bad/missing studyId etc.
 *
 * 409 already_completed → /already-completed
 * 409 study_closed      → /study-closed
 */
type Stage =
  | { kind: "loading" }
  | { kind: "admin-email-form"; studyId: string; deviceId?: string; error?: string }
  | { kind: "entry"; interviewId: string; study: ParticipantStudyView }
  | { kind: "active"; interviewId: string; study: ParticipantStudyView }
  | { kind: "error"; message: string };

export function Interview() {
  const [searchParams] = useSearchParams();
  const [stage, setStage] = useState<Stage>({ kind: "loading" });

  // Resolve which flow we're in on mount. Three valid shapes:
  //
  //   ?d=<base64>                 — legacy Customer.io blob (kept working
  //                                 for any older campaigns still in flight)
  //   ?studyId=X&email=Y          — plain Customer.io email link (current
  //                                 shape: `email` baked into the Liquid
  //                                 template, `studyId` baked into the
  //                                 campaign)
  //   ?studyId=X                  — admin QA: no email in URL, show the
  //                                 email entry form so admins can pretend
  //                                 to be any participant for testing
  //
  // Anything else → terminal error.
  useEffect(() => {
    const d = searchParams.get("d");
    const plainStudyId = searchParams.get("studyId");
    const plainEmail = searchParams.get("email");
    // Device id from the link (e.g. ?d=...&deviceId=xxx). Forwarded to initiate
    // so the API can store it on the user's identity_snapshot. Optional — most
    // links won't carry it.
    const deviceId = searchParams.get("deviceId") ?? undefined;

    // Remember how we got here so the /too-short "Do again" button can return
    // to /interview WITH the original params (studyId / d blob). Without this
    // it navigates to a bare /interview → "Missing study id".
    if (d || plainStudyId) {
      try {
        sessionStorage.setItem("interview_entry_search", searchParams.toString());
      } catch {
        /* storage blocked — retry just falls back to bare /interview */
      }
    }

    if (d) {
      const payload = decodeAutologin(d);
      if (!payload) {
        setStage({ kind: "error", message: "Invalid interview link" });
        return;
      }
      void runInitiate(payload.email, payload.studyId, setStage, deviceId);
      return;
    }
    if (plainStudyId && plainEmail) {
      // Plain ?studyId=X&email=Y — same flow as the base64 blob, just
      // without the URL obfuscation. Architecture §5.1: no sensitive PII
      // here, no HMAC needed.
      void runInitiate(plainEmail.trim(), plainStudyId, setStage, deviceId);
      return;
    }
    if (plainStudyId) {
      setStage({ kind: "admin-email-form", studyId: plainStudyId, deviceId });
      return;
    }
    setStage({ kind: "error", message: "Missing study id" });
  }, [searchParams]);

  switch (stage.kind) {
    case "loading":
      return <LoadingShell />;
    case "admin-email-form":
      return (
        <AdminEmailForm
          studyId={stage.studyId}
          deviceId={stage.deviceId}
          setStage={setStage}
        />
      );
    case "entry":
      return (
        <EntryScreen
          interviewId={stage.interviewId}
          study={stage.study}
          onBegin={() =>
            setStage({
              kind: "active",
              interviewId: stage.interviewId,
              study: stage.study,
            })
          }
        />
      );
    case "active":
      return (
        <ActiveCall interviewId={stage.interviewId} study={stage.study} />
      );
    case "error":
      return <ErrorShell message={stage.message} />;
  }
}

// ── Initiate helper ─────────────────────────────────────────────────────────

async function runInitiate(
  email: string,
  studyId: string,
  setStage: (s: Stage) => void,
  deviceId?: string,
): Promise<void> {
  try {
    const { interviewId, userId, study } = await initiateInterview(
      email,
      studyId,
      deviceId,
    );
    // Real participant (autologin / plain email link) — not a test.
    setTestMode(false);
    saveSession({ email, studyId, interviewId, userId, isTest: false });
    // Track ONLY when we have a Leaply user id (participant exists in
    // identity.users). No id → no Amplitude init → every event stays a
    // no-op. Never track our internal UUID or anonymous device_id as a
    // "real" participant.
    if (userId) {
      initAmplitude(userId);
      events.mainViewed({
        studyId,
        study_slug: study.slug,
        plan_target: study.planTarget,
      });
    }
    setStage({ kind: "entry", interviewId, study });
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.code === "already_completed") {
        window.location.assign("/already-completed");
        return;
      }
      if (err.code === "study_closed") {
        window.location.assign("/study-closed");
        return;
      }
    }
    setStage({
      kind: "error",
      message:
        err instanceof Error ? err.message : "Failed to start your interview",
    });
  }
}

// ── Saving overlay ───────────────────────────────────────────────────────────
// Shown while the recording uploads + transcript saves after End (seconds on
// a long interview). Rotating status lines so it feels alive and the wait is
// legible; the lead line + "don't close" stay fixed.

const SAVING_TICKER = [
  "Saving your conversation…",
  "Analyzing your answers…",
  "Pulling out the key themes…",
  "Tidying up your results…",
  "Almost there…",
];

function SavingOverlay() {
  const [i, setI] = useState(0);
  useEffect(() => {
    // Advance, then hold on the last line (don't loop back to the start —
    // looping reads as "stuck"; a held "Almost there…" reads as "nearly done").
    const t = setInterval(
      () => setI((n) => Math.min(n + 1, SAVING_TICKER.length - 1)),
      2400,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-full flex flex-col items-center gap-6 py-10 text-center">
      <span
        aria-hidden
        className="inline-block w-10 h-10 rounded-full border-[3px] border-brand-100 border-t-brand-700 animate-spin"
      />
      <div>
        <p
          key={i}
          aria-live="polite"
          className="font-serif text-[22px] sm:text-[26px] text-surface-ink mb-1.5 animate-karaoke-in"
        >
          {SAVING_TICKER[i]}
        </p>
        <p className="text-body-m text-surface-subtle max-w-[32ch] mx-auto leading-relaxed">
          This can take a few seconds — please don't close the page.
        </p>
      </div>
    </div>
  );
}

// ── Shells ──────────────────────────────────────────────────────────────────

function LoadingShell() {
  return (
    <div className="w-full max-w-[860px] mx-auto bg-surface rounded-card shadow-card p-12 text-center text-body-m text-surface-subtle">
      Getting your interview ready…
    </div>
  );
}

function ErrorShell({ message }: { message: string }) {
  return (
    <div className="w-full max-w-[560px] mx-auto text-center">
      <h1 className="font-serif text-title-m text-surface-ink mb-3">
        Something went wrong
      </h1>
      <p className="text-body-m text-surface-subtle mb-2">{message}</p>
      <p className="text-body-s text-surface-subtle">
        If you arrived here from a Leaply email, try opening the link again
        from your inbox.
      </p>
    </div>
  );
}

// ── Admin-QA email form ─────────────────────────────────────────────────────

function AdminEmailForm({
  studyId,
  deviceId,
  setStage,
}: {
  studyId: string;
  deviceId?: string;
  setStage: (s: Stage) => void;
}) {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validationError = validateEmail(email);
  const showError = touched && validationError !== null;
  const valid = email.trim().length > 0 && isValidEmail(email);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { interviewId, userId, study } = await initiateInterview(
        email.trim(),
        studyId,
        deviceId,
      );
      // Admin-QA path (?studyId only, email typed in) — a dev/QA run, NOT a
      // real participant. We do NOT init Amplitude and fire NO events at all:
      // track() stays a no-op while uninitialised, so the whole admin session
      // (here + ActiveCall + /complete) emits nothing into the funnel.
      // isTest persists in the session so /complete also skips init.
      setTestMode(true);
      saveSession({
        email: email.trim(),
        studyId,
        interviewId,
        userId,
        isTest: true,
      });
      setStage({ kind: "entry", interviewId, study });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "already_completed") {
          window.location.assign("/already-completed");
          return;
        }
        if (err.code === "study_closed") {
          window.location.assign("/study-closed");
          return;
        }
      }
      setError(
        err instanceof Error ? err.message : "Failed to start your interview",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-[860px] mx-auto bg-surface rounded-card shadow-card overflow-hidden">
      <div className="pt-12 sm:pt-16">
        <InterviewHero />
      </div>
      <div className="relative z-10 bg-surface px-8 sm:px-20 pt-2 pb-12 sm:pb-16 -mt-32 sm:-mt-40">
        <h1 className="font-serif text-balance text-[26px] leading-[32px] sm:text-[32px] sm:leading-[40px] text-center text-surface-ink mb-3 sm:mb-4 mx-auto max-w-[18ch]">
          Welcome to the AI interview with Lea
        </h1>
        <p className="text-center text-body-m text-surface-subtle mb-6 sm:mb-7 mx-auto max-w-[44ch] leading-relaxed">
          Thanks for being here. Find a quiet spot and let Lea guide you through
          a <span className="whitespace-nowrap">15–20</span> minute voice chat
          at your own pace.
        </p>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="you@example.com"
              aria-label="Your email"
              aria-invalid={showError || undefined}
              className={`flex-1 min-w-0 rounded-pill border bg-surface px-5 py-3 text-body-m text-surface-ink placeholder:text-surface-subtle/60 focus:outline-none focus:ring-2 transition ${
                showError
                  ? "border-red-500 focus:border-red-600 focus:ring-red-200"
                  : "border-brand-100 focus:border-brand-500 focus:ring-brand-200"
              }`}
            />
            <button
              type="submit"
              disabled={!valid || submitting}
              className="w-full sm:w-auto sm:min-w-[200px] shrink-0 inline-flex items-center justify-center gap-2 rounded-pill bg-surface-ink text-white h-12 px-6 text-body-s font-medium hover:bg-surface-ink/90 active:bg-surface-ink disabled:bg-surface-ink/30 disabled:cursor-not-allowed transition-colors"
            >
              <span>{submitting ? "…" : "Start"}</span>
              {!submitting && <ArrowIcon />}
            </button>
          </div>
          {showError && (
            <p role="alert" className="text-body-s text-red-700 px-2">
              {validationError}
            </p>
          )}
          {error && (
            <p className="text-body-s text-red-700 px-2" role="alert">
              {error}
            </p>
          )}
        </form>
        <InterviewDisclaimers />
      </div>
    </div>
  );
}

// ── Entry screen (post-initiate, pre-Begin) ─────────────────────────────────

function EntryScreen({
  interviewId: _interviewId,
  study,
  onBegin,
}: {
  interviewId: string;
  study: ParticipantStudyView;
  onBegin: () => void;
}) {
  return (
    <div className="w-full max-w-[860px] mx-auto bg-surface rounded-card shadow-card overflow-hidden">
      <div className="pt-12 sm:pt-16">
        <InterviewHero />
      </div>
      <div className="relative z-10 bg-surface px-8 sm:px-20 pt-2 pb-12 sm:pb-16 -mt-32 sm:-mt-40">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-full h-12 sm:h-16 bg-gradient-to-t from-surface to-transparent"
        />
        <h1 className="font-serif text-balance text-[26px] leading-[32px] sm:text-[32px] sm:leading-[40px] text-center text-surface-ink mb-3 sm:mb-4 mx-auto max-w-[18ch]">
          Welcome to the AI interview with {study.interviewerName}
        </h1>
        <p className="text-center text-body-m text-surface-subtle mb-6 sm:mb-7 mx-auto max-w-[44ch] leading-relaxed">
          Thanks for being here. Find a quiet spot and let{" "}
          {study.interviewerName} guide you through a{" "}
          <span className="whitespace-nowrap">15–20</span> minute voice chat at
          your own pace.
        </p>
        <div className="flex justify-center">
          <button
            type="button"
            // Navigation only — the begin event fires on the CALL screen's
            // Begin button, at the moment the call actually starts.
            onClick={onBegin}
            className="w-full sm:w-auto sm:min-w-[280px] inline-flex items-center justify-center gap-2 rounded-pill bg-surface-ink text-white h-12 px-6 text-body-s font-medium hover:bg-surface-ink/90 active:bg-surface-ink transition-colors"
          >
            {/* "Start" = go to the call screen (navigation only). The actual
                call begins — and interviewBeginClicked fires — on the call
                screen's "Begin" button. */}
            <span>Start</span>
            <ArrowIcon />
          </button>
        </div>
        <InterviewDisclaimers />
      </div>
    </div>
  );
}

// ── Active call ─────────────────────────────────────────────────────────────

function ActiveCall({
  interviewId,
  study,
}: {
  interviewId: string;
  study: ParticipantStudyView;
}) {
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement>(null);
  const startTimeRef = useRef<number | null>(null);
  // True from the End click until navigation — stop() uploads the audio
  // (tens of MB on a long interview) + PATCHes the transcript, which can take
  // several seconds. endingRef guards against double-clicks / racing
  // auto-ends; `ending` drives the saving overlay.
  const endingRef = useRef(false);
  const [ending, setEnding] = useState(false);

  const {
    status,
    paused,
    error,
    level,
    assistantText,
    userSpeaking,
    connectionLost,
    timeUp,
    agentEnded,
    start,
    stop,
    pause,
    resume,
  } = useRealtimeAgent({
    studyId: study.id,
    interviewId,
    audioRef,
    maxDurationMinutes: study.maxDurationMinutes,
  });

  // NO auto-start (matches the prototype): the call connects only when the
  // participant explicitly clicks Begin on THIS screen — mic permission and
  // Lea's greeting shouldn't jump at them on mount. The begin event fires
  // here, at the moment the call actually starts.
  function startCall() {
    events.beginClicked({
      studyId: study.id,
      study_slug: study.slug,
      plan_target: study.planTarget,
    });
    startTimeRef.current = Date.now();
    void start();
  }

  // Connection died mid-call (network drop / OpenAI closed the session).
  // Auto-end so the transcript-so-far is saved and the participant lands on
  // /complete (or /too-short) instead of staring at a frozen orb.
  const autoEndedRef = useRef(false);
  useEffect(() => {
    if (!connectionLost || autoEndedRef.current) return;
    autoEndedRef.current = true;
    events.connectionLost({
      studyId: study.id,
      study_slug: study.slug,
      plan_target: study.planTarget,
    });
    void endCall("connection_lost");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionLost]);

  // Duration cap reached (study.maxDurationMinutes): Lea already delivered
  // her wrap-up goodbye a minute ago — end and save like a normal End click.
  useEffect(() => {
    if (!timeUp || autoEndedRef.current) return;
    autoEndedRef.current = true;
    // endCall fires interviewEndClicked with end_reason="time_up".
    void endCall("time_up");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeUp]);

  // Lea hung up herself (end_interview tool after her goodbye) — end and
  // save exactly like a normal End click. endCall fires interviewEndClicked
  // with end_reason="agent_ended".
  useEffect(() => {
    if (!agentEnded || autoEndedRef.current) return;
    autoEndedRef.current = true;
    void endCall("agent_ended");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentEnded]);

  const isLive = status === "connected" || status === "connecting";

  async function endCall(reason: string = "user") {
    if (endingRef.current) return; // guard double-click / racing auto-ends
    endingRef.current = true;
    setEnding(true);
    events.endClicked({
      studyId: study.id,
      study_slug: study.slug,
      plan_target: study.planTarget,
      end_reason: reason,
    });
    const status = await stop();
    const elapsedSeconds = startTimeRef.current
      ? (Date.now() - startTimeRef.current) / 1000
      : 0;
    if (elapsedSeconds < VALID_MIN_SECONDS) {
      sessionStorage.setItem("interview_failed", "1");
      navigate("/too-short");
      return;
    }
    // Reward eligibility from the server's validity verdict. Only an explicit
    // completed_invalid (spam / too-few-turns / min-words gate) denies the
    // reward; an undefined status (save glitch) defaults to eligible so a
    // real participant is never wrongly denied. The async LLM substance
    // downgrade lands after this — those interviews still show the card, but
    // rewards are sent manually from completed_valid rows so it's caught.
    sessionStorage.setItem(
      "interview_reward",
      status === "completed_invalid" ? "0" : "1",
    );
    navigate("/complete");
  }

  function handlePauseResume() {
    if (paused) resume();
    else pause();
  }

  const label: Record<AgentStatus, string> = {
    idle: "Begin",
    connecting: "Connecting…",
    connected: "End",
    ended: "Ended",
    error: "Try again",
  };

  // Saving overlay — shown while the recording uploads + transcript saves
  // (seconds on a long interview). Replaces the live call UI so it's clear
  // something is happening and the End button can't be hit again.
  if (ending) {
    return <SavingOverlay />;
  }

  return (
    <div className="relative w-full flex flex-col items-center gap-4 sm:gap-8">
      <Orb
        active={isLive && !paused}
        level={paused ? 0 : level}
        onClick={
          status === "connected"
            ? () => void endCall()
            : status === "idle" || status === "error"
              ? startCall
              : undefined
        }
        disabled={status === "connecting"}
        ariaLabel={label[status]}
      />

      {/* Fixed height stops the layout jumping as captions stream in, but
          170px starved small phones — the orb + this + buttons must all fit
          a ~640px viewport (Slack in-app browser). Before the call starts
          (idle) there's no caption to reserve for, so we collapse this and
          show the Begin hint instead — that keeps the button above the fold. */}
      {status === "idle" ? (
        <BeginHint />
      ) : (
        <div
          className="w-full max-w-[640px] px-2 h-[110px] sm:h-[170px] overflow-hidden"
          aria-live="polite"
          aria-atomic="false"
        >
          {userSpeaking ? <Listening /> : <LiveCaption text={assistantText} />}
        </div>
      )}

      <div className="flex items-center gap-3">
        {status === "connected" && (
          <button
            type="button"
            onClick={handlePauseResume}
            aria-label={paused ? "Resume the interview" : "Pause the interview"}
            title={paused ? "Resume" : "Pause"}
            className="inline-flex items-center justify-center w-12 h-12 min-h-[48px] rounded-full bg-surface border-2 border-brand-700 text-brand-700 shadow-[0_8px_20px_-6px_rgba(14,90,55,0.35)] hover:bg-brand-50 hover:border-brand-800 hover:text-brand-800 active:bg-brand-100 active:scale-95 transition-all"
          >
            {paused ? (
              <svg width="18" height="18" viewBox="0 0 12 12" aria-hidden>
                <path d="M3 2 L10 6 L3 10 Z" fill="currentColor" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 12 12" aria-hidden>
                <rect
                  x="3"
                  y="2"
                  width="2.4"
                  height="8"
                  rx="0.6"
                  fill="currentColor"
                />
                <rect
                  x="6.6"
                  y="2"
                  width="2.4"
                  height="8"
                  rx="0.6"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        )}
        <button
          type="button"
          onClick={status === "connected" ? () => void endCall() : startCall}
          disabled={status === "connecting"}
          className="inline-flex items-center gap-2.5 rounded-pill bg-brand-700 text-white px-5 sm:px-6 min-h-[48px] py-3 text-body-m font-medium shadow-card hover:bg-brand-800 active:bg-brand-900 transition-colors disabled:opacity-60"
        >
          <span
            aria-hidden
            className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${
              status === "connected" ? "bg-red-500" : "bg-white/95"
            }`}
          >
            {status === "connected" ? (
              <span className="w-2 h-2 bg-white rounded-[2px]" />
            ) : (
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                <path
                  d="M3 1.6v6.8M5 .8v8.4M7 2.6v4.8"
                  stroke="#0e5a37"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </span>
          <span>{label[status]}</span>
        </button>
      </div>

      {error && (
        <p
          className="text-body-s text-red-700 max-w-md text-center px-4"
          role="alert"
        >
          {error}
        </p>
      )}

      <audio ref={audioRef} autoPlay playsInline className="hidden" />
    </div>
  );
}

/** Pre-call affordance: a tooltip-style pill + a bouncing down-arrow that
 *  points at the Begin button just below it. Shown only while idle so a
 *  participant who lands on the big orb knows what to do next (the button can
 *  otherwise read as "just decoration", and the orb fills the screen). */
function BeginHint() {
  return (
    <div className="flex flex-col items-center gap-1.5 select-none">
      <div className="inline-flex items-center gap-1.5 rounded-pill bg-surface-ink/90 text-white px-3.5 py-1.5 text-body-s font-medium shadow-card">
        <span>
          Press <span className="font-semibold">Begin</span> to start
        </span>
      </div>
      <svg
        width="20"
        height="20"
        viewBox="0 0 16 16"
        aria-hidden
        className="text-surface-ink/80 motion-safe:animate-bounce"
      >
        <path
          d="M8 3v9m0 0 4-4m-4 4-4-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M3 8h10m-4-4 4 4-4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
